import React, { useRef, useState, useEffect } from 'react';
import { Typography, Stack, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getFunctions, httpsCallable } from 'firebase/functions';

import unionLogo from '../../../../../../assets/unionLogo.png';
import { setEmployeeInfo } from '../../../../../../redux/slices/currentUserSlice';

import {
  PinStepContainer,
  LogoContainer,
  PinInputContainer,
  StyledPinInput,
  HelpTextContainer,
} from './UnionLoginPinStep.styles';

const UnionLoginPinStep = ({ onSuccess }) => {
  const [pin, setPin] = useState(Array(4).fill(''));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const selectedVenue = useSelector((state) => state.venues.selectedVenueId);
  const dispatch = useDispatch();
  const inputsRef = useRef([]);
  const errorTimeoutRef = useRef(null);

  const clearErrorAfterTimeout = () => {
    // Clear any previous timers
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  // Clear the timer if the component is unmounted
  useEffect(() => {
    const timeoutId = errorTimeoutRef.current;

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Its used to add the Pins
  const handleChange = (index, value) => {
    const sanitizedValue = value.slice(0, 1).replace(/[^0-9]/g, ''); // Limit to a single number
    const newPin = [...pin];
    newPin[index] = sanitizedValue;
    setPin(newPin);

    if (sanitizedValue && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }

    if (newPin.join('').length === 4) {
      submitPin(newPin);
    }
  };

  const handleKeyDown = (index, event) => {
    const { key } = event;

    if (key === 'Backspace') {
      // Remove the value from the current field and move focus to the previous field
      const newPin = [...pin];
      newPin[index] = '';
      setPin(newPin);
      if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (key === 'ArrowRight') {
      // Move focus to the next field if it exists
      if (index < 3) {
        inputsRef.current[index + 1]?.focus();
      }
    } else if (key === 'ArrowLeft') {
      // Move focus to the previous field if it exists
      if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  // Its used to copy and paste the pin
  const handlePaste = (event) => {
    const pasteData = event.clipboardData.getData('text');

    if (/^\d{4}$/.test(pasteData)) {
      setPin(pasteData.split(''));
      inputsRef.current[3]?.focus(); // Focus on the last field
      submitPin(pasteData.split(''));
    } else {
      setError('Invalid PIN format. Please enter 4 digits.');
      event.preventDefault();
    }
  };

  const submitPin = async (pinArray) => {
    if (!selectedVenue) {
      setError('Please select a venue before entering the PIN.');
      clearErrorAfterTimeout(); // Clear the error automatically
      return;
    }

    const pinCode = pinArray.join('');
    setIsLoading(true);
    try {
      const functions = getFunctions();
      const linkUnionAccount = httpsCallable(functions, 'linkEmployeeFromUnionPinAndVenue');
      const result = await linkUnionAccount({ vid: selectedVenue, pin: pinCode });

      if (result?.data) {
        dispatch(setEmployeeInfo(result.data));
        setPin(Array(4).fill('')); // Clear PIN
        onSuccess?.();
      } else {
        setError('Invalid PIN or venue. Please try again.');
      }
    } catch (error) {
      setError('Failed to link account. Please validate your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PinStepContainer>
      <LogoContainer>
        <img src={unionLogo} alt="Union Logo" style={{ width: '100px' }} />
      </LogoContainer>

      <Stack>
        <Typography variant="title3" align="center" sx={{ marginBottom: '8px' }} gutterBottom>
          Enter your login PIN
        </Typography>
        <Typography variant="bodyRegular" align="center" color={'#EBEBF599'}>
          This is the same PIN you use to access the Union POS in restaurant.
        </Typography>
      </Stack>

      <PinInputContainer onPaste={handlePaste}>
        {pin.map((value, index) => (
          <StyledPinInput
            key={index}
            inputRef={(el) => (inputsRef.current[index] = el)}
            type="tel"
            value={value}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onChange={(e) => handleChange(index, e.target.value)}
            aria-label={`PIN digit ${index + 1}`}
            autoComplete="off"
            disabled={isLoading} // Deshabilita los inputs mientras carga
          />
        ))}
      </PinInputContainer>

      {/* Indicador de carga debajo de los inputs */}
      {isLoading && (
        <Stack justifyContent="center" alignItems="center" sx={{ margin: '10px 0' }}>
          <CircularProgress size={20} color="inherit" />
        </Stack>
      )}

      {/* Mensaje de error */}
      {error && (
        <Typography variant="body2" color="error" align="center" sx={{ marginTop: '10px' }}>
          {error}
        </Typography>
      )}

      <HelpTextContainer>
        <Typography variant="footNoteEmphasized" align="center" color="#EBEBF599">
          Need help?
        </Typography>
        <Typography variant="footNoteRegular" align="center" color="#EBEBF599">
          Contact your manager and request your Union login PIN.
        </Typography>
      </HelpTextContainer>
    </PinStepContainer>
  );
};

export default UnionLoginPinStep;
